import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';

export type NotificationVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';

interface DLONotificationProps {
  message: string;
  variant?: NotificationVariant;
  visible?: boolean;
  onDismiss?: () => void;
  style?: any;
}

const DLONotification: React.FC<DLONotificationProps> = ({
  message,
  variant = 'primary',
  visible = true,
  onDismiss,
  style,
}) => {
  if (!visible) {
    return null;
  }

  const getVariantStyles = (variant: NotificationVariant) => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: '#CCE7FF', // Light blue background
          textColor: '#004085', // Dark blue text
        };
      case 'secondary':
        return {
          backgroundColor: '#E2E3E5', // Light gray background
          textColor: '#383D41', // Dark gray text
        };
      case 'success':
        return {
          backgroundColor: '#C3E6CB', // Light green background
          textColor: '#155724', // Dark green text
        };
      case 'danger':
        return {
          backgroundColor: '#F5C6CB', // Light red background
          textColor: '#721C24', // Dark red text
        };
      case 'warning':
        return {
          backgroundColor: '#FFF3CD', // Light yellow background
          textColor: '#856404', // Dark yellow/brown text
        };
      case 'info':
        return {
          backgroundColor: '#BEE5EB', // Light cyan background
          textColor: '#0C5460', // Dark cyan text
        };
      case 'light':
        return {
          backgroundColor: '#FEFEFE', // Very light gray background
          textColor: '#6C757D', // Medium gray text
        };
      case 'dark':
        return {
          backgroundColor: '#C6C8CA', // Medium gray background
          textColor: '#1B1E21', // Very dark gray text
        };
      default:
        return {
          backgroundColor: '#CCE7FF',
          textColor: '#004085',
        };
    }
  };

  const variantStyles = getVariantStyles(variant);

  return (
    <View 
      style={[
        styles.notification,
        { backgroundColor: variantStyles.backgroundColor },
        style
      ]}
    >
      <Text 
        style={[
          styles.message,
          { color: variantStyles.textColor }
        ]}
      >
        {message}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  notification: {
    padding: 16,
    borderRadius: 8,
    marginVertical: 8,
    marginHorizontal: 16,
    borderWidth: 1,
    borderColor: 'transparent',
    shadowColor: 'rgba(0, 0, 0, 0.1)',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 1,
    shadowRadius: 4,
    elevation: 2,
  },
  message: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '400',
    textAlign: 'left',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
});

export default DLONotification;